import React, { FC, lazy } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { useGlobalConfigContext } from 'core/contexts/GlobalConfig';
import { Module } from '@fides/fides-react-components';
import { addAndEditPageAccessGranted as hasUserEditorAccess } from 'components/pages/UserManagement/Users/userPageAccessHelper';
import { addAndEditPageAccessGranted as hasUserGroupEditorAccess } from 'components/pages/UserManagement/UserGroups/userGroupsAccessHelper';
import { PrivateRoute } from '../helpers';

const UserManagement = lazy(() => import('../../components/pages/UserManagement/Users'));
const UserGroups = lazy(() => import('../../components/pages/UserManagement/UserGroups'));
const ViewUserGroup = lazy(
  () => import('../../components/pages/UserManagement/UserGroups/UserGroupDetails/UserGroupViewPage')
);
const UserGroupEditor = lazy(
  () => import('../../components/pages/UserManagement/UserGroups/UserGroupDetails/UserGroupEditor')
);
const ViewUserDetails = lazy(() => import('../../components/pages/UserManagement/Users/UserDetails/ViewUserDetails'));
const AddUser = lazy(() => import('../../components/pages/UserManagement/Users/UserDetails/AddUser'));
const EditUser = lazy(() => import('../../components/pages/UserManagement/Users/UserDetails/EditUser'));

export const UserManagementRouter: FC = () => {
  const { accessHelper, user } = useGlobalConfigContext();
  const defaultTab: string =
    accessHelper.hasAccessForModule(Module.USER) || accessHelper.isAnyFidesAdmin() ? 'users' : 'user-groups';
  const hasAccessToUserGroupsPage: boolean =
    accessHelper.hasAccessForModule(Module.USERGROUP) || accessHelper.isImpersonatedAsClient;
  const hasAccessToUsersPage: boolean =
    accessHelper.hasAccessForModule(Module.USER) ||
    (accessHelper.isAnyFidesAdmin() && !accessHelper.isImpersonated) ||
    accessHelper.isImpersonatedAsClient;

  return (
    <Switch>
      <Redirect from='/userManagement/' to={`/userManagement/${defaultTab}`} exact />
      <PrivateRoute path='/userManagement/users' exact>
        {hasAccessToUsersPage ? <UserManagement /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/users/editor/:uniqueId' exact>
        {hasUserEditorAccess(accessHelper, user) ? <EditUser /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/users/editor' exact>
        {hasUserEditorAccess(accessHelper, user) ? <AddUser /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/users/:uniqueId' exact>
        {hasAccessToUsersPage ? <ViewUserDetails /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/user-groups' exact>
        {hasAccessToUserGroupsPage ? <UserGroups /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/user-groups/editor/:uniqueId?' exact>
        {hasUserGroupEditorAccess(accessHelper, user) ? <UserGroupEditor /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <PrivateRoute path='/userManagement/user-groups/:uniqueId' exact>
        {hasAccessToUserGroupsPage ? <ViewUserGroup /> : <Redirect to='/page-not-found' />}
      </PrivateRoute>
      <Redirect to='/page-not-found' />
    </Switch>
  );
};
